// Network capture (PRD §3.1) — the authoritative "what is the user actually querying"
// signal. Instead of guessing filter state from the (many, inconsistent) filter UIs, we
// capture the real data request the app sends to the backend. This fires on EVERY dashboard
// data fetch regardless of UI, default-vs-interaction, deep-link or auto-load — the ground
// truth of what was requested.
//
// Emits `data_query` with the endpoint (= which dashboard/dataset) and the request body
// (= the full filter set, incl. SKU). We wrap window.fetch and never disturb the request: errors
// propagate, capture is fail-silent. While Dipi is served by both backends, v1 (/api/…) and v2
// (/api/v2/…) calls are captured alike — the question is what the user asked for, not who served it.

import { config } from "./config"
import { flushBeacon } from "./transport"

let started = false

const MAX_BODY = 4000

function shouldCapture(url, method) {
    if ((method || "GET").toUpperCase() !== "POST") return false
    const u = String(url || "")
    if (!u.includes("/api/")) return false                        // v1 (/api/…) and v2 (/api/v2/…) data calls
    if (u.includes("/api/v2/events")) return false                // anti-recursion: never capture our own ingest
    if (/excel|download|export|shorten|short-url/i.test(u)) return false // downloads and share links aren't "queries"
    if (LOOKUPS.test(u)) return false                            // plumbing, not something the user asked for
    return true
}

// Light dedup: one user submit fans out into several parallel gateway calls; collapse
// identical (endpoint, body) within a short window so we don't emit N near-duplicates.
// Calls the app makes on its own to set a screen up — session checks, selector options, saved
// filters, per-user defaults. Measured on 2026-09-03: without this list every screen load emitted
// two or three `data_query` rows before the user touched anything. What remains is the dashboard
// data the user actually requested (defaults and deep-links included, which is the point).
// Matches the v1 and the v2 prefix alike (`/api/master-data/…` and `/api/v2/master-data/…`).
const LOOKUPS = /\/api\/(v2\/)?(session|master-data|admin\/user-default)\//

// The logout call. `user_signed_out` has to leave the browser BEFORE this request is answered:
// the app clears its user state only after the server has killed the session, so anything the
// SDK emits on that state change flushes without a valid cookie and dies with a 401 (measured
// 2026-09-03). Emitting here, as the request goes out, is the last moment the cookie still works.
// Best-effort: the beacon and the logout race on the server, but the beacon is queued first.
const LOGOUT = /\/api\/(v2\/)?session\/logout/

let recent = []
function isDuplicate(key, now) {
    recent = recent.filter(r => now - r.ts < 2000)
    if (recent.some(r => r.key === key)) return true
    recent.push({ key, ts: now })
    return false
}

export function startNetworkCapture(track) {
    if (started || typeof window === "undefined" || !window.fetch) return
    started = true
    const original = window.fetch

    window.fetch = async (url, options = {}) => {
        try {
            const u0 = typeof url === "string" ? url : (url && url.url) || ""
            if (LOGOUT.test(u0)) { track("user_signed_out"); flushBeacon() }
        } catch { /* fail-silent */ }
        // Let the request (and any error) flow through untouched.
        const response = await original(url, options)
        try {
            const u = typeof url === "string" ? url : (url && url.url) || ""
            const method = options?.method || (typeof url === "object" && url?.method) || "GET"
            if (shouldCapture(u, method)) {
                const path = u.split("?")[0]
                const rawBody = typeof options?.body === "string" ? options.body : null
                const now = new Date().getTime()
                const key = path + "|" + (rawBody ? rawBody.slice(0, 500) : "")
                if (!isDuplicate(key, now)) {
                    let filters
                    if (rawBody && rawBody.length <= MAX_BODY) {
                        try {
                            filters = JSON.parse(rawBody)
                            // Identity already rides on the envelope (user_id / customer_id).
                            if (filters && typeof filters === "object") {
                                delete filters.userID
                                delete filters.customerID
                            }
                        } catch { /* non-JSON body — leave filters undefined */ }
                    }
                    track("data_query", { endpoint: path, filters, status: response.status })
                }
            }
        } catch { /* fail-silent — never disturb the app's fetch */ }
        return response
    }
    if (config.debug) console.debug("[analytics] network capture installed")
}
